'use client';

import { useEffect, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { checkUserSubscriptionStatus } from '../../subscription/functions/checkUserSubscriptionStatus';
import {
  type CurrentUserSubscriptionState,
  deriveSubscriptionState,
} from '../../subscription/functions/currentUserSubscriptionState';

export type UserCurrentSubscriptionOfCreator = {
  subscribedLevel: CurrentUserSubscriptionState['accessLevel'];
  activeSubscriptionLevel: CurrentUserSubscriptionState['activeSubscriptionLevel'];
  canceled: CurrentUserSubscriptionState['canceled'];
  queuedSubscription: CurrentUserSubscriptionState['queuedSubscription'];
};

/**
 * The signed-in viewer's subscription standing with one creator, for the
 * subscribe picker's gating. Ports PopshopRN's `useUserCurrentSubscriptionOfCreator`:
 * it exposes the derived {@link CurrentUserSubscriptionState} — `accessLevel`
 * (highest tier they can access, incl. a canceled-but-still-valid sub),
 * `activeSubscriptionLevel` (what they're paying for now; drives Subscribe vs
 * Upgrade), `canceled`, and any `queuedSubscription`.
 *
 * Fetches `checkUserSubscriptionStatus(userId, creatorId)` once the viewer id is
 * known and derives the state client-side (the server checkout engine uses the
 * same pair). `isLoading` is true until the first fetch resolves.
 */
export function useUserCurrentSubscriptionOfCreator(creatorId: string) {
  const userId = useCurrentUserId();
  const [state, setState] = useState<CurrentUserSubscriptionState>();

  useEffect(() => {
    if (!userId || !creatorId) return;
    const controller = new AbortController();
    let cancelled = false;
    checkUserSubscriptionStatus({
      userId,
      creatorId,
      signal: controller.signal,
    })
      .then(data => {
        if (!cancelled) setState(deriveSubscriptionState(data));
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        console.error(
          '[useUserCurrentSubscriptionOfCreator] status fetch failed:',
          error,
        );
        // Fall back to "no subscription" rather than blocking the picker.
        if (!cancelled) setState(deriveSubscriptionState(null));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, creatorId]);

  // Anonymous viewer: never loading, never subscribed.
  const isLoading = userId != null && state == null;

  const currentSubscriptionInfo: UserCurrentSubscriptionOfCreator | undefined =
    state == null || state.accessLevel == null
      ? undefined
      : {
          subscribedLevel: state.accessLevel,
          activeSubscriptionLevel: state.activeSubscriptionLevel,
          canceled: state.canceled,
          queuedSubscription: state.queuedSubscription,
        };

  return {
    isLoading,
    currentSubscriptionInfo,
    queuedSubscription: state?.queuedSubscription,
  };
}
